import { useCallback, useEffect, useRef } from 'react';
import { enqueueSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import type { User } from '../contexts/auth.types';

/**
 * How often a tab showing an impersonation asks the database whether it is still honoured.
 *
 * The database ends an admin's impersonation 60 minutes after it started
 * (`get_cached_impersonated_user_id()`, which `sync_user_session` follows since migration
 * 20260922011000). The client never computes that deadline itself; it only has to notice the end
 * within one interval, so the banner and the database cannot disagree for long.
 */
export const IMPERSONATION_CHECK_INTERVAL_MS = 60_000;

export const impersonationEndedMessage = (name: string) =>
  `Impersonation of ${name} has ended (it expired or was stopped in another tab). You're signed in as yourself again.`;

/**
 * Asks `sync_user_session` — the RPC the session is built from — whether `adminId` is still
 * impersonating. `null` means "unknown" (RPC error or no row), so a transient failure never ends
 * an impersonation on the client.
 */
export async function checkImpersonationActive(adminId: string): Promise<boolean | null> {
  try {
    const { data, error } = await supabase.rpc('sync_user_session', { user_id: adminId }).single();
    if (error || !data) return null;
    return Boolean((data as { impersonated_by_admin_id?: string | null }).impersonated_by_admin_id);
  } catch {
    return null;
  }
}

interface ImpersonatedSession {
  adminId: string;
  name: string;
}

/**
 * Keeps the web session honest about an admin's impersonation.
 *
 * 1. While the session is impersonating, re-check every {@link IMPERSONATION_CHECK_INTERVAL_MS}
 *    (visible tabs only; the auth hook already reloads the profile when a tab becomes visible)
 *    and reload the profile as soon as the database says the impersonation is over.
 * 2. Whenever the session drops out of impersonation back to the admin — from that check, a tab
 *    becoming visible, a token refresh, or any other profile reload — that was not the admin's own
 *    "Exit Impersonation", confirm it with the database, then say so in a snackbar and take the
 *    admin to /admin/users, exactly where "Exit Impersonation" lands. If the database says the
 *    impersonation is still on (a degraded profile load dropped it), reload instead of announcing.
 *
 * Call `markExplicitStop(true)` before an intentional stop (and `false` if it fails) so the
 * intentional exit is not announced as an expiry.
 */
export function useImpersonationExpiryWatcher(
  user: User | null,
  refreshUser: () => Promise<void>,
) {
  const navigate = useNavigate();
  const explicitStopRef = useRef(false);
  const lastImpersonationRef = useRef<ImpersonatedSession | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const adminId = user?.impersonated_by_admin_id ?? null;

  useEffect(() => {
    if (!adminId) return undefined;

    let cancelled = false;
    let inFlight = false;

    const check = async () => {
      if (inFlight || document.visibilityState === 'hidden') return;
      inFlight = true;
      try {
        const active = await checkImpersonationActive(adminId);
        if (!cancelled && active === false) {
          await refreshUser();
        }
      } finally {
        inFlight = false;
      }
    };

    const timer = window.setInterval(() => {
      void check();
    }, IMPERSONATION_CHECK_INTERVAL_MS);

    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [adminId, refreshUser]);

  useEffect(() => {
    const previous = lastImpersonationRef.current;
    lastImpersonationRef.current = user?.impersonated_by_admin_id
      ? { adminId: user.impersonated_by_admin_id, name: user.name }
      : null;

    // Only a session that WAS impersonating and is now the admin themself (not a sign-out, not
    // another account) has left an impersonation.
    if (!previous || !user || user.impersonated_by_admin_id || user.id !== previous.adminId) return;

    if (explicitStopRef.current) {
      explicitStopRef.current = false;
      return;
    }

    void (async () => {
      const active = await checkImpersonationActive(previous.adminId);
      if (!mountedRef.current) return;
      if (active === true) {
        await refreshUser();
        return;
      }
      if (active === null) return;
      enqueueSnackbar(impersonationEndedMessage(previous.name), {
        variant: 'warning',
        autoHideDuration: 10_000,
      });
      navigate('/admin/users');
    })();
  }, [user, navigate, refreshUser]);

  const markExplicitStop = useCallback((stopping: boolean) => {
    explicitStopRef.current = stopping;
  }, []);

  return { markExplicitStop };
}
